import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  limits: null,
  lastFetched: null,
};

const slice = createSlice({
  name: 'userLimits',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET LIMITS SUCCESS
    getLimitsSuccess(state, action) {
      state.isLoading = false;
      state.limits = action.payload;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // RESET LIMITS
    resetLimits(state) {
      state.limits = null;
      state.lastFetched = null;
      state.error = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { resetLimits } = slice.actions;

// ----------------------------------------------------------------------

/**
 * Fetch user plan limits from backend
 *
 * Returns all limits in a single API call:
 * - Organizations, batches, storage, recipes, spawn sources
 * - Current usage vs maximum allowed
 * - Feature access flags
 * - Translated messages based on user's language
 */
export function getUserLimits() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/sporeuser/get_plan_limits/');
      dispatch(slice.actions.getLimitsSuccess(response.data));
    } catch (error) {
      console.error('Failed to fetch user limits:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch limits'));
    }
  };
}

// ----------------------------------------------------------------------

/**
 * Refresh user limits after a mutation
 * Only refreshes if data is stale (more than 5 seconds old)
 */
export function refreshLimitsIfNeeded() {
  return async (dispatch, getState) => {
    const { userLimits } = getState();
    const FIVE_SECONDS = 5 * 1000;

    // Only refresh if data is stale or doesn't exist
    const isStale = !userLimits.lastFetched ||
                    (Date.now() - userLimits.lastFetched > FIVE_SECONDS);

    if (isStale) {
      return dispatch(getUserLimits());
    }
  };
}

// ----------------------------------------------------------------------

/**
 * Force refresh user limits (ignores staleness check)
 * Use this after creating/deleting resources
 */
export function forceRefreshLimits() {
  return getUserLimits();
}

// ----------------------------------------------------------------------

// Selectors
export const selectUserLimits = (state) => state.userLimits;

export const selectIsLoading = (state) => state.userLimits.isLoading;

export const selectPlan = (state) => state.userLimits.limits?.plan || 'free';

export const selectIsPro = (state) => state.userLimits.limits?.is_pro || false;

export const selectOrganizationLimit = (state) =>
  state.userLimits.limits?.limits?.organizations || null;

export const selectActiveBatchLimit = (state) =>
  state.userLimits.limits?.limits?.active_batches || null;

export const selectStorageLimit = (state) =>
  state.userLimits.limits?.limits?.storage || null;

export const selectRecipeLimit = (state) =>
  state.userLimits.limits?.limits?.recipes || null;

export const selectSpawnSourceLimit = (state) =>
  state.userLimits.limits?.limits?.spawn_sources || null;

export const selectAlertLimit = (state) =>
  state.userLimits.limits?.limits?.active_alerts || null;

export const selectOrganizationMemberLimit = (state) =>
  state.userLimits.limits?.limits?.organization_members || null;

export const selectFeatures = (state) =>
  state.userLimits.limits?.features || {};

export const selectCanCreateOrganization = (state) =>
  state.userLimits.limits?.limits?.organizations?.allowed ?? true;

export const selectCanCreateBatch = (state) =>
  state.userLimits.limits?.limits?.active_batches?.allowed ?? true;

export const selectCanUploadFile = (state) =>
  state.userLimits.limits?.limits?.storage?.allowed ?? true;

export const selectCanCreateRecipe = (state) =>
  state.userLimits.limits?.limits?.recipes?.allowed ?? true;

export const selectCanCreateSpawnSource = (state) =>
  state.userLimits.limits?.limits?.spawn_sources?.allowed ?? true;

export const selectCanCreateAlert = (state) =>
  state.userLimits.limits?.limits?.active_alerts?.allowed ?? true;

export const selectCanInviteMember = (state) =>
  state.userLimits.limits?.limits?.organization_members?.allowed ?? true;
